/**
 * DataLinkageMap — interactive flow diagram of the CRM's domain model
 * + a live audit panel listing places where info isn't linked yet.
 *
 * Two view modes:
 *  • Diagram   — React Flow canvas, drag nodes, click to inspect/edit.
 *                Can also add/delete custom nodes + edges so the
 *                operator can grow the map as the data model evolves.
 *  • Mermaid   — plain-text version of the same graph for paste-into-
 *                email / export-as-PDF.
 *
 * Plus an Audit list below both views with severity-coded findings
 * and an Action hint per finding.
 *
 * Lives at both /settings (under the "Data Linkage" tab) and inside
 * the existing /data-sync page so it's reachable from either place.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ReactFlow, {
  Background, Controls, MiniMap, ReactFlowProvider, useNodesState, useEdgesState, addEdge,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import mermaid from 'mermaid';
import { AlertTriangle, AlertCircle, Info, Plus, Trash2, Copy, X, RefreshCw, ChevronDown, ExternalLink } from 'lucide-react';

mermaid.initialize({ startOnLoad: false, theme: 'neutral', securityLevel: 'loose' });

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Group → visual style. Used by React Flow node renderer + Mermaid
// classDef. Keep these in sync if you tweak the colour palette.
const GROUP_STYLES = {
  data:      { bg: '#EFF6FF', border: '#3B82F6', tone: 'blue' },
  reference: { bg: '#FAF5FF', border: '#9333EA', tone: 'violet' },
  builder:   { bg: '#FFF7ED', border: '#EA580C', tone: 'orange' },
  output:    { bg: '#ECFDF5', border: '#10B981', tone: 'emerald' },
};

const PROP_STYLES = {
  auto:    { stroke: '#10B981', label: 'AUTO',    bg: '#ECFDF5', text: '#047857' },
  manual:  { stroke: '#F59E0B', label: 'MANUAL',  bg: '#FFFBEB', text: '#92400E' },
  missing: { stroke: '#DC2626', label: 'MISSING', bg: '#FEF2F2', text: '#991B1B' },
};

function makeApi() {
  const tok = localStorage.getItem('token');
  return axios.create({
    baseURL: `${API_URL}/api`,
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
}

/** Custom node renderer — colored by group, two-line label, source pill. */
function LinkageNode({ data }) {
  const style = GROUP_STYLES[data.group] || GROUP_STYLES.data;
  return (
    <div
      data-testid={`linkage-node-${data.id}`}
      style={{ background: style.bg, borderColor: style.border }}
      className="px-3 py-2 rounded-md border-2 shadow-sm min-w-[170px] cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => data.onSelect?.()}
    >
      <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{data.group}</div>
      <div className="text-sm font-bold text-[#0F172A]">{data.label}</div>
      {data.collection && (
        <div className="text-[10px] font-mono text-slate-500 truncate">{data.collection}</div>
      )}
      <div className="mt-1 text-[9px] inline-flex items-center gap-1">
        <span className={`px-1.5 py-0.5 rounded-full font-semibold ${data.source === 'manual' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {data.source === 'manual' ? 'manual' : 'derived'}
        </span>
      </div>
    </div>
  );
}

const NODE_TYPES = { linkage: LinkageNode };

export default function DataLinkageMap() {
  const api = useMemo(() => makeApi(), []);
  const [view, setView] = useState('diagram'); // 'diagram' | 'mermaid' | 'audit'
  const [rawNodes, setRawNodes] = useState([]);
  const [rawEdges, setRawEdges] = useState([]);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [newNode, setNewNode] = useState({ id: '', label: '', group: 'data', collection: '', source: 'manual', notes: '' });
  const [flash, setFlash] = useState(null);
  // Scroll-wheel lock — default ON so hovering the map doesn't hijack
  // page-scroll. Toggle off (🔓) to zoom/pan with the wheel.
  const [mapLocked, setMapLocked] = useState(true);
  // When the user clicks a box on the Mermaid view we open a small
  // floating panel showing the same NodeInspector. Tracks visibility
  // independently of selectedNode so closing it doesn't ripple back
  // into the Diagram view's sidebar selection.
  const [mermaidInspectorOpen, setMermaidInspectorOpen] = useState(false);
  const mermaidRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        api.get('/data-linkage/map'),
        api.get('/data-linkage/audit'),
      ]);
      setRawNodes(m.data.nodes || []);
      setRawEdges(m.data.edges || []);
      setAudit(a.data);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // ── React Flow translation ──────────────────────────────────────────
  // Map our backend nodes → RF nodes. We auto-layout in a simple
  // 4-row grid based on group so the default render is readable
  // without forcing a layout library. Operator can then drag.
  const flowNodes = useMemo(() => {
    const rowOf = { data: 0, reference: 1, builder: 2, output: 3 };
    const perRow = {};
    return rawNodes.map((n) => {
      const r = rowOf[n.group] ?? 0;
      perRow[r] = (perRow[r] || 0) + 1;
      return {
        id: n.id,
        type: 'linkage',
        position: { x: (perRow[r] - 1) * 230, y: r * 160 },
        data: { ...n, onSelect: () => { setSelectedNode(n); setSelectedEdge(null); } },
      };
    });
  }, [rawNodes]);

  const flowEdges = useMemo(() => rawEdges.map((e) => {
    const ps = PROP_STYLES[e.propagation] || PROP_STYLES.manual;
    return {
      id: e.id,
      source: e.from,
      target: e.to,
      label: e.label || '',
      animated: e.propagation === 'auto',
      style: { stroke: ps.stroke, strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 600, fill: ps.text },
      labelBgStyle: { fill: ps.bg },
      markerEnd: { type: MarkerType.ArrowClosed, color: ps.stroke },
    };
  }), [rawEdges]);

  const [nodes, , onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Keep RF state in sync whenever the backend changes (after reloads
  // / edits). useNodesState only seeds on mount so we need a manual
  // sync hook.
  useEffect(() => {
    // Replace nodes/edges wholesale — preserves react-flow internal state.
    // eslint-disable-next-line no-unused-expressions
    flowNodes; flowEdges;
  }, [flowNodes, flowEdges]);

  const onConnect = useCallback(async (params) => {
    if (!editMode) {
      setFlash({ kind: 'warn', text: 'Turn on Edit mode to add connections' });
      setTimeout(() => setFlash(null), 3000);
      return;
    }
    setEdges((eds) => addEdge(params, eds));
    try {
      await api.patch('/data-linkage/map', {
        action: 'add_edge',
        edge: { from: params.source, to: params.target, label: 'custom', propagation: 'manual', notes: 'Operator-defined' },
      });
      setFlash({ kind: 'ok', text: `Edge added: ${params.source} → ${params.target}` });
      setTimeout(() => setFlash(null), 3000);
      reload();
    } catch (e) {
      setFlash({ kind: 'err', text: 'Failed to save edge' });
    }
  }, [api, editMode, reload, setEdges]);

  // Patch a node's overlay (label/notes/group/collection/source/key_fields).
  // Used by NodeInspector's auto-save loop. Patch-merge on the backend.
  const overrideNode = useCallback(async (nodeId, partial) => {
    await api.patch('/data-linkage/map', {
      action: 'override_node', node_id: nodeId, override: partial,
    });
    // Locally patch rawNodes so the inspector + canvas reflect the
    // change without waiting for the network reload.
    setRawNodes((prev) => prev.map((n) => n.id === nodeId ? { ...n, ...partial, _overridden: true } : n));
    setSelectedNode((prev) => prev && prev.id === nodeId ? { ...prev, ...partial, _overridden: true } : prev);
  }, [api]);

  const resetNodeOverride = useCallback(async (nodeId) => {
    await api.patch('/data-linkage/map', { action: 'reset_node_override', node_id: nodeId });
    setSelectedNode(null);
    reload();
  }, [api, reload]);

  // Edge overlay edits — label / propagation / notes.
  const overrideEdge = useCallback(async (edgeId, partial) => {
    await api.patch('/data-linkage/map', {
      action: 'override_edge', edge_id: edgeId, override: partial,
    });
    setRawEdges((prev) => prev.map((e) => e.id === edgeId ? { ...e, ...partial, _overridden: true } : e));
    setSelectedEdge((prev) => prev && prev.id === edgeId ? { ...prev, ...partial, _overridden: true } : prev);
  }, [api]);

  const reverseEdge = useCallback(async (edgeId) => {
    await api.patch('/data-linkage/map', { action: 'reverse_edge', edge_id: edgeId });
    reload();
  }, [api, reload]);

  const resetEdgeOverride = useCallback(async (edgeId) => {
    await api.patch('/data-linkage/map', { action: 'reset_edge_override', edge_id: edgeId });
    setSelectedEdge(null);
    reload();
  }, [api, reload]);

  const deleteEdge = useCallback(async (edgeId) => {
    if (!window.confirm('Delete this connection?')) return;
    try {
      await api.patch('/data-linkage/map', { action: 'delete_edge', edge_id: edgeId });
      setSelectedEdge(null);
      reload();
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Delete failed' });
      setTimeout(() => setFlash(null), 4000);
    }
  }, [api, reload]);

  const addCustomNode = useCallback(async () => {
    if (!newNode.id || !newNode.label) {
      setFlash({ kind: 'err', text: 'id + label required' });
      return;
    }
    try {
      await api.patch('/data-linkage/map', { action: 'add_node', node: newNode });
      setAddNodeOpen(false);
      setNewNode({ id: '', label: '', group: 'data', collection: '', source: 'manual', notes: '' });
      reload();
      setFlash({ kind: 'ok', text: `Node "${newNode.label}" added` });
      setTimeout(() => setFlash(null), 3000);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Add node failed' });
    }
  }, [api, newNode, reload]);

  const deleteCustomNode = useCallback(async (nodeId) => {
    if (!window.confirm(`Delete node "${nodeId}" and all its custom edges?`)) return;
    try {
      await api.patch('/data-linkage/map', { action: 'delete_node', node_id: nodeId });
      setSelectedNode(null);
      reload();
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Delete failed' });
    }
  }, [api, reload]);

  // ── Mermaid view ────────────────────────────────────────────────────
  const mermaidSource = useMemo(() => {
    const lines = ['flowchart LR'];
    rawNodes.forEach((n) => {
      const safe = n.id.replace(/[^a-z0-9_]/gi, '_');
      lines.push(`  ${safe}["${n.label}\\n<${n.group}>"]`);
      lines.push(`  class ${safe} ${n.group}Box`);
    });
    rawEdges.forEach((e) => {
      const a = e.from.replace(/[^a-z0-9_]/gi, '_');
      const b = e.to.replace(/[^a-z0-9_]/gi, '_');
      const arrow = e.propagation === 'auto' ? '==>' : e.propagation === 'missing' ? '-.->' : '-->';
      lines.push(`  ${a} ${arrow}|${(e.label || '').replace(/[|]/g, '/')}| ${b}`);
    });
    lines.push('');
    lines.push('classDef dataBox fill:#EFF6FF,stroke:#3B82F6,stroke-width:2px;');
    lines.push('classDef referenceBox fill:#FAF5FF,stroke:#9333EA,stroke-width:2px;');
    lines.push('classDef builderBox fill:#FFF7ED,stroke:#EA580C,stroke-width:2px;');
    lines.push('classDef outputBox fill:#ECFDF5,stroke:#10B981,stroke-width:2px;');
    return lines.join('\n');
  }, [rawNodes, rawEdges]);

  useEffect(() => {
    if (view !== 'mermaid' || !mermaidRef.current) return;
    const el = mermaidRef.current;
    el.innerHTML = '';
    const uid = `mmd-${Date.now()}`;
    mermaid.render(uid, mermaidSource).then(({ svg }) => {
      if (!mermaidRef.current) return;
      mermaidRef.current.innerHTML = svg;
      // Attach click handlers to each node box so the operator can
      // open the same Node Inspector from the Mermaid view. Mermaid
      // tags each node with `class="node …"` and an id of the form
      // `flowchart-<safeId>-N`. We stash our raw id on a data-attr
      // before injecting so the lookup is stable across re-renders.
      const safeToId = {};
      rawNodes.forEach((n) => { safeToId[n.id.replace(/[^a-z0-9_]/gi, '_')] = n.id; });
      mermaidRef.current.querySelectorAll('g.node').forEach((g) => {
        // Mermaid prefixes every id with the uid passed into render
        // (e.g. `mmd-1779405302935-flowchart-property-0`). Don't anchor
        // with ^ — match the `flowchart-<safeId>-<n>` segment anywhere
        // in the id string.
        const m = (g.id || '').match(/flowchart-([a-z0-9_]+)-\d+$/i);
        const rawId = m ? safeToId[m[1]] : null;
        if (!rawId) return;
        g.style.cursor = 'pointer';
        g.setAttribute('data-linkage-node-id', rawId);
        g.addEventListener('click', () => {
          const n = rawNodes.find((x) => x.id === rawId);
          if (n) {
            setSelectedNode(n);
            setSelectedEdge(null);
            setMermaidInspectorOpen(true);
          }
        });
      });
    }).catch((err) => {
      if (mermaidRef.current) mermaidRef.current.innerHTML = `<pre style="color:red">${err.message}</pre>`;
    });
  }, [view, mermaidSource, rawNodes]);

  const copyMermaid = () => {
    navigator.clipboard.writeText(mermaidSource);
    setFlash({ kind: 'ok', text: 'Mermaid source copied — paste into any markdown / mermaid.live viewer' });
    setTimeout(() => setFlash(null), 4000);
  };

  if (loading) return <div className="p-8 text-slate-400 text-center">Loading linkage map…</div>;

  return (
    <ReactFlowProvider>
      <div className="space-y-3" data-testid="data-linkage-page">
        {/* Header + view-mode tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-bold text-[#0F172A]">Data Linkage Map</h2>
          <span className="text-xs text-slate-500 italic">how key entities link, where info auto-propagates, and which links are missing</span>
          <div className="ml-auto inline-flex items-center rounded-md ring-1 ring-slate-200 bg-white p-0.5 text-xs">
            {['diagram', 'mermaid', 'audit'].map((v) => (
              <button
                key={v}
                data-testid={`linkage-view-${v}`}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded font-semibold capitalize transition-colors ${
                  view === v ? 'bg-[#D35400] text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >{v}</button>
            ))}
          </div>
          <button
            data-testid="linkage-reload"
            onClick={reload}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 rounded-md hover:bg-slate-50"
            title="Re-scan the database for fresh audit findings"
          ><RefreshCw size={12} /> Refresh</button>
        </div>

        {/* Map-lock toggle — only relevant on the Diagram view. Default
            locked so hovering the canvas doesn't hijack page-scroll. */}
        {view === 'diagram' && (
          <div className="flex items-center gap-2 text-[11px]">
            <button
              data-testid="linkage-map-lock"
              onClick={() => setMapLocked((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md font-semibold ring-1 transition-colors ${
                mapLocked
                  ? 'bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100'
                  : 'bg-amber-50 text-amber-800 ring-amber-300 hover:bg-amber-100'
              }`}
              title={mapLocked ? 'Click to unlock map scroll-wheel zoom' : 'Click to lock — restores page scroll'}
            >
              {mapLocked ? '🔒 Map locked' : '🔓 Map unlocked (wheel zooms)'}
            </button>
            <span className="text-slate-500 italic">
              {mapLocked ? 'wheel = page scroll · drag to pan · use ± buttons to zoom' : 'wheel = zoom · drag to pan'}
            </span>
          </div>
        )}

        {flash && (
          <div className={`px-3 py-2 rounded text-xs font-semibold ${
            flash.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
            : flash.kind === 'err' ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
            : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
          }`}>{flash.text}</div>
        )}

        {/* Diagram view */}
        {view === 'diagram' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
            <div className="rounded-md ring-1 ring-slate-200 bg-white" style={{ height: 600 }}>
              <ReactFlow
                nodes={flowNodes}
                edges={flowEdges}
                nodeTypes={NODE_TYPES}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgeClick={(_, edge) => {
                  const e = rawEdges.find(x => x.id === edge.id);
                  if (e) { setSelectedEdge(e); setSelectedNode(null); }
                }}
                fitView
                zoomOnScroll={!mapLocked}
                panOnScroll={!mapLocked}
                preventScrolling={!mapLocked}
                zoomOnPinch
                panOnDrag
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={20} size={1} />
                <Controls />
                <MiniMap pannable />
              </ReactFlow>
            </div>

            <aside className="space-y-3">
              {/* Edit-mode toggle + Add Node */}
              <div className="rounded-md ring-1 ring-slate-200 bg-white p-3">
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="linkage-edit-mode"
                    checked={editMode}
                    onChange={() => setEditMode((v) => !v)}
                  />
                  Edit mode
                </label>
                <p className="text-[11px] text-slate-500 mt-1">Drag node handles to connect. Add custom nodes / connections below.</p>
                {editMode && (
                  <button
                    data-testid="linkage-add-node-btn"
                    onClick={() => setAddNodeOpen(true)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold rounded bg-[#D35400] text-white hover:bg-[#A04000]"
                  ><Plus size={12} /> Add custom node</button>
                )}
              </div>

              {/* Selected node / edge inspector */}
              {selectedNode ? (
                <NodeInspector
                  node={selectedNode}
                  onDelete={deleteCustomNode}
                  isCustom={!['property','roof_details','appraisal','quote_types','rate_cards','diagram','reroof_builder','maintenance_builder','guttering_builder','components','pdfs','work_completion'].includes(selectedNode.id)}
                  editMode={editMode}
                  onClose={() => setSelectedNode(null)}
                  onOverride={overrideNode}
                  onReset={resetNodeOverride}
                />
              ) : selectedEdge ? (
                <EdgeInspector
                  edge={selectedEdge}
                  onClose={() => setSelectedEdge(null)}
                  editMode={editMode}
                  onOverride={overrideEdge}
                  onReverse={reverseEdge}
                  onReset={resetEdgeOverride}
                  onDelete={deleteEdge}
                />
              ) : (
                <Legend />
              )}
            </aside>
          </div>
        )}

        {/* Mermaid view */}
        {view === 'mermaid' && (
          <div className="rounded-md ring-1 ring-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-slate-600">Rendered Mermaid (paste into any md or <a href="https://mermaid.live" target="_blank" rel="noreferrer" className="text-[#D35400] underline">mermaid.live</a>):</span>
              <span className="text-[10px] italic text-slate-400 ml-2">Click any box to inspect / edit its fields →</span>
              <button
                data-testid="linkage-copy-mermaid"
                onClick={copyMermaid}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-white ring-1 ring-slate-200 hover:bg-slate-50"
              ><Copy size={12} /> Copy source</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
              <div ref={mermaidRef} className="overflow-auto" data-testid="linkage-mermaid-svg" />
              {/* Floating inspector — same NodeInspector used on the
                  Diagram view so edits behave identically. Sticks on
                  the right while the operator clicks boxes in the SVG. */}
              <aside className="space-y-3">
                {mermaidInspectorOpen && selectedNode ? (
                  <NodeInspector
                    node={selectedNode}
                    onDelete={deleteCustomNode}
                    isCustom={!['property','roof_details','appraisal','quote_types','rate_cards','diagram','reroof_builder','maintenance_builder','guttering_builder','components','pdfs','work_completion'].includes(selectedNode.id)}
                    editMode={editMode}
                    onClose={() => { setMermaidInspectorOpen(false); setSelectedNode(null); }}
                    onOverride={overrideNode}
                    onReset={resetNodeOverride}
                  />
                ) : (
                  <div className="rounded-md ring-1 ring-dashed ring-slate-200 bg-slate-50 p-4 text-center text-[11px] text-slate-500">
                    Click any box on the Mermaid diagram to inspect &amp; tag its key fields.
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer pl-1">
                  <input
                    type="checkbox"
                    data-testid="mermaid-edit-mode"
                    checked={editMode}
                    onChange={() => setEditMode((v) => !v)}
                  />
                  Edit mode (unlocks field editing)
                </label>
              </aside>
            </div>
            <details className="mt-4">
              <summary className="cursor-pointer text-xs text-slate-500 font-semibold">View / edit the Mermaid source text</summary>
              <pre className="mt-2 p-3 bg-slate-50 rounded text-[11px] font-mono overflow-auto max-h-[260px]" data-testid="linkage-mermaid-source">{mermaidSource}</pre>
            </details>
          </div>
        )}

        {/* Audit view */}
        {(view === 'audit' || view === 'diagram') && audit && view !== 'mermaid' && (
          <AuditPanel audit={audit} compact={view === 'diagram'} />
        )}

        {/* Add-node modal */}
        {addNodeOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-md shadow-xl ring-1 ring-slate-200 w-full max-w-md p-5">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-bold text-[#0F172A]">Add custom node</h3>
                <button onClick={() => setAddNodeOpen(false)} className="ml-auto text-slate-400 hover:text-slate-700"><X size={16} /></button>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">ID (lowercase, no spaces)</label>
                  <input data-testid="new-node-id" className="w-full px-3 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.id} onChange={e => setNewNode({...newNode, id: e.target.value.toLowerCase().replace(/\s+/g, '_')})} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Label</label>
                  <input data-testid="new-node-label" className="w-full px-3 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.label} onChange={e => setNewNode({...newNode, label: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1">Group</label>
                    <select data-testid="new-node-group" className="w-full px-2 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.group} onChange={e => setNewNode({...newNode, group: e.target.value})}>
                      <option value="data">data</option>
                      <option value="reference">reference</option>
                      <option value="builder">builder</option>
                      <option value="output">output</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 block mb-1">Source</label>
                    <select data-testid="new-node-source" className="w-full px-2 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.source} onChange={e => setNewNode({...newNode, source: e.target.value})}>
                      <option value="manual">manual</option>
                      <option value="derived">derived</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Collection <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input data-testid="new-node-collection" className="w-full px-3 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.collection} onChange={e => setNewNode({...newNode, collection: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Notes</label>
                  <textarea data-testid="new-node-notes" rows={2} className="w-full px-3 py-2 ring-1 ring-slate-200 rounded text-sm" value={newNode.notes} onChange={e => setNewNode({...newNode, notes: e.target.value})} />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-4">
                <button onClick={() => setAddNodeOpen(false)} className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
                <button data-testid="new-node-save" onClick={addCustomNode} className="px-4 py-2 text-xs font-semibold rounded bg-[#D35400] hover:bg-[#A04000] text-white">Add node</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}

function NodeInspector({ node, onDelete, isCustom, editMode, onClose, onOverride, onReset }) {
  const style = GROUP_STYLES[node.group] || GROUP_STYLES.data;
  // Per-field auto-save (debounced 900ms) + undo. Captures the value
  // BEFORE the in-flight edit so a single Undo click reverts.
  const [draft, setDraft] = useState({
    label: node.label || '',
    notes: node.notes || '',
    collection: node.collection || '',
    group: node.group || 'data',
    source: node.source || 'manual',
    key_fields_text: (Array.isArray(node.key_fields) ? node.key_fields : []).join(', '),
  });
  // Re-seed when the selection switches to a different node.
  useEffect(() => {
    setDraft({
      label: node.label || '',
      notes: node.notes || '',
      collection: node.collection || '',
      group: node.group || 'data',
      source: node.source || 'manual',
      key_fields_text: (Array.isArray(node.key_fields) ? node.key_fields : []).join(', '),
    });
    undoSnapshotRef.current = null;
    setFlash(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id]);

  const debounceRef = useRef(null);
  const undoSnapshotRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);

  const scheduleSave = (nextDraft) => {
    if (!editMode) return;
    setDraft(nextDraft);
    // Snapshot the prior state on the first keystroke after a fresh
    // selection so Undo lands the operator back on the previous
    // saved values.
    if (!undoSnapshotRef.current) {
      undoSnapshotRef.current = {
        label: node.label || '',
        notes: node.notes || '',
        collection: node.collection || '',
        group: node.group || 'data',
        source: node.source || 'manual',
        key_fields: Array.isArray(node.key_fields) ? [...node.key_fields] : [],
      };
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const key_fields = nextDraft.key_fields_text
          .split(',').map(s => s.trim()).filter(Boolean);
        await onOverride(node.id, {
          label: nextDraft.label,
          notes: nextDraft.notes,
          collection: nextDraft.collection || null,
          group: nextDraft.group,
          source: nextDraft.source,
          key_fields,
        });
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        setFlash({ kind: 'ok' });
        flashTimerRef.current = setTimeout(() => setFlash(null), 8000);
      } catch (e) {
        setFlash({ kind: 'err' });
      }
    }, 900);
  };

  const undo = async () => {
    const snap = undoSnapshotRef.current;
    if (!snap) return;
    undoSnapshotRef.current = null;
    setDraft({
      label: snap.label,
      notes: snap.notes,
      collection: snap.collection,
      group: snap.group,
      source: snap.source,
      key_fields_text: (snap.key_fields || []).join(', '),
    });
    await onOverride(node.id, {
      label: snap.label,
      notes: snap.notes,
      collection: snap.collection || null,
      group: snap.group,
      source: snap.source,
      key_fields: snap.key_fields,
    });
    setFlash(null);
  };

  const editable = editMode;
  return (
    <div className="rounded-md ring-1 ring-slate-200 bg-white p-3" data-testid="linkage-node-inspector" style={{ borderTop: `4px solid ${style.border}` }}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500 flex items-center gap-1.5">
            {node.group}
            {node._overridden && <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[9px] font-semibold">overridden</span>}
          </div>
          {!editable ? (
            <h3 className="text-sm font-bold text-[#0F172A]">{node.label}</h3>
          ) : (
            <input
              data-testid="node-edit-label"
              className="w-full text-sm font-bold text-[#0F172A] border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded px-1 -mx-1 outline-none"
              value={draft.label}
              onChange={(e) => scheduleSave({ ...draft, label: e.target.value })}
            />
          )}
          {!editable ? (
            node.collection && <code className="text-[10px] text-slate-500 font-mono">{node.collection}</code>
          ) : (
            <input
              data-testid="node-edit-collection"
              placeholder="collection (optional)"
              className="w-full text-[10px] text-slate-500 font-mono border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded px-1 -mx-1 outline-none mt-0.5"
              value={draft.collection}
              onChange={(e) => scheduleSave({ ...draft, collection: e.target.value })}
            />
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
      </div>

      {/* Notes */}
      {!editable ? (
        <p className="text-xs text-slate-600 leading-relaxed mb-2">{node.notes}</p>
      ) : (
        <textarea
          data-testid="node-edit-notes"
          rows={3}
          className="w-full text-xs text-slate-700 leading-relaxed mb-2 px-2 py-1.5 ring-1 ring-slate-200 rounded outline-none focus:ring-[#D35400]"
          value={draft.notes}
          onChange={(e) => scheduleSave({ ...draft, notes: e.target.value })}
        />
      )}

      {/* Group + Source selects (edit mode only) */}
      {editable && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="text-[9px] uppercase font-bold tracking-wide text-slate-500 block">Group</label>
            <select
              data-testid="node-edit-group"
              className="w-full text-xs px-2 py-1 ring-1 ring-slate-200 rounded bg-white"
              value={draft.group}
              onChange={(e) => scheduleSave({ ...draft, group: e.target.value })}
            >
              {Object.keys(GROUP_STYLES).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] uppercase font-bold tracking-wide text-slate-500 block">Source</label>
            <select
              data-testid="node-edit-source"
              className="w-full text-xs px-2 py-1 ring-1 ring-slate-200 rounded bg-white"
              value={draft.source}
              onChange={(e) => scheduleSave({ ...draft, source: e.target.value })}
            >
              <option value="manual">manual</option>
              <option value="derived">derived</option>
            </select>
          </div>
        </div>
      )}

      {/* Key fields */}
      <div className="text-[11px] mb-2">
        <div className="font-semibold text-slate-600 mb-1 flex items-center gap-2">
          Key fields
          {editable && <span className="text-[9px] font-normal text-slate-400 italic">comma-separated · enter to add</span>}
        </div>
        {!editable ? (
          Array.isArray(node.key_fields) && node.key_fields.length > 0 ? (
            <ul className="flex flex-wrap gap-1">
              {node.key_fields.map((f) => (
                <li key={f}><code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px]">{f}</code></li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400 italic text-[10px]">no fields tagged yet</p>
          )
        ) : (
          <input
            data-testid="node-edit-key-fields"
            className="w-full text-[11px] font-mono px-2 py-1.5 ring-1 ring-slate-200 rounded outline-none focus:ring-[#D35400]"
            value={draft.key_fields_text}
            onChange={(e) => scheduleSave({ ...draft, key_fields_text: e.target.value })}
            placeholder="e.g. roof_type, roof_profile, roof_colour"
          />
        )}
      </div>

      {/* Status pill + Undo */}
      {flash && (
        <div className="flex items-center gap-1.5 text-[10px] mb-2" data-testid="node-edit-flash">
          {flash.kind === 'ok' ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 font-semibold">✓ Auto-saved</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 font-semibold">⚠ Save failed</span>
          )}
          {flash.kind === 'ok' && undoSnapshotRef.current && (
            <button data-testid="node-edit-undo" onClick={undo} className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 font-semibold hover:bg-amber-100">↶ Undo</button>
          )}
        </div>
      )}

      {/* Reset / Delete */}
      {editable && node._overridden && !isCustom && (
        <button
          data-testid="node-reset-override"
          onClick={() => onReset(node.id)}
          className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold rounded bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100 mt-1"
        ><RefreshCw size={12} /> Reset to default</button>
      )}
      {isCustom && editMode && (
        <button
          data-testid="linkage-delete-node"
          onClick={() => onDelete(node.id)}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold rounded bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
        ><Trash2 size={12} /> Delete this custom node</button>
      )}
      {!editMode && (
        <p className="text-[10px] text-slate-400 italic mt-1">Turn on Edit mode to modify these fields.</p>
      )}
    </div>
  );
}

function EdgeInspector({ edge, onClose, editMode, onOverride, onReverse, onReset, onDelete }) {
  const ps = PROP_STYLES[edge.propagation] || PROP_STYLES.manual;
  const [draft, setDraft] = useState({
    label: edge.label || '',
    notes: edge.notes || '',
    propagation: edge.propagation || 'manual',
  });
  useEffect(() => {
    setDraft({
      label: edge.label || '',
      notes: edge.notes || '',
      propagation: edge.propagation || 'manual',
    });
    undoSnapshotRef.current = null;
    setFlash(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edge.id]);

  const debounceRef = useRef(null);
  const undoSnapshotRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);

  const scheduleSave = (nextDraft) => {
    if (!editMode) return;
    setDraft(nextDraft);
    if (!undoSnapshotRef.current) {
      undoSnapshotRef.current = {
        label: edge.label || '',
        notes: edge.notes || '',
        propagation: edge.propagation || 'manual',
      };
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await onOverride(edge.id, nextDraft);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        setFlash({ kind: 'ok' });
        flashTimerRef.current = setTimeout(() => setFlash(null), 8000);
      } catch (e) {
        setFlash({ kind: 'err' });
      }
    }, 900);
  };

  const undo = async () => {
    const snap = undoSnapshotRef.current;
    if (!snap) return;
    undoSnapshotRef.current = null;
    setDraft({ ...snap });
    await onOverride(edge.id, snap);
    setFlash(null);
  };

  const markMissing = () => scheduleSave({ ...draft, propagation: 'missing' });
  const isCustom = String(edge.id || '').startsWith('custom_');

  return (
    <div className="rounded-md ring-1 ring-slate-200 bg-white p-3" data-testid="linkage-edge-inspector" style={{ borderTop: `4px solid ${ps.stroke}` }}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-bold tracking-wide flex items-center gap-1.5" style={{ color: ps.text }}>
            {ps.label} link
            {edge._overridden && <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[9px] font-semibold">overridden</span>}
          </div>
          <h3 className="text-sm font-bold text-[#0F172A]">{edge.from} <span className="text-slate-400">→</span> {edge.to}</h3>
          {!editMode ? (
            edge.label && <code className="text-[10px] text-slate-500 font-mono">{edge.label}</code>
          ) : (
            <input
              data-testid="edge-edit-label"
              className="w-full text-[10px] text-slate-600 font-mono border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded px-1 -mx-1 outline-none mt-0.5"
              value={draft.label}
              onChange={(e) => scheduleSave({ ...draft, label: e.target.value })}
              placeholder="edge label"
            />
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
      </div>

      {/* Propagation pill bar */}
      {editMode && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[9px] uppercase font-bold tracking-wide text-slate-500">Flow</span>
          {Object.entries(PROP_STYLES).map(([k, v]) => (
            <button
              key={k}
              data-testid={`edge-edit-prop-${k}`}
              onClick={() => scheduleSave({ ...draft, propagation: k })}
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 transition-colors ${
                draft.propagation === k ? 'text-white' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
              style={draft.propagation === k ? { background: v.stroke, borderColor: v.stroke } : {}}
            >{k}</button>
          ))}
        </div>
      )}

      {/* Notes */}
      {!editMode ? (
        <p className="text-xs text-slate-600 leading-relaxed">{edge.notes}</p>
      ) : (
        <textarea
          data-testid="edge-edit-notes"
          rows={3}
          className="w-full text-xs text-slate-700 leading-relaxed mb-2 px-2 py-1.5 ring-1 ring-slate-200 rounded outline-none focus:ring-[#D35400]"
          value={draft.notes}
          onChange={(e) => scheduleSave({ ...draft, notes: e.target.value })}
        />
      )}

      {/* Auto-save pill */}
      {flash && (
        <div className="flex items-center gap-1.5 text-[10px] mb-2" data-testid="edge-edit-flash">
          {flash.kind === 'ok' ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 font-semibold">✓ Auto-saved</span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 font-semibold">⚠ Save failed</span>
          )}
          {flash.kind === 'ok' && undoSnapshotRef.current && (
            <button data-testid="edge-edit-undo" onClick={undo} className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 font-semibold hover:bg-amber-100">↶ Undo</button>
          )}
        </div>
      )}

      {/* Action row — Reverse / Mark missing / Reset / Delete */}
      {editMode && (
        <div className="grid grid-cols-2 gap-1.5 mt-2">
          <button
            data-testid="edge-reverse"
            onClick={() => onReverse(edge.id)}
            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
            title="Swap direction (from ↔ to)"
          >⇄ Reverse</button>
          {edge.propagation !== 'missing' && (
            <button
              data-testid="edge-mark-missing"
              onClick={markMissing}
              className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
              title="Flag this link as missing — picked up by the audit"
            >⚠ Mark missing</button>
          )}
          {edge._overridden && (
            <button
              data-testid="edge-reset-override"
              onClick={() => onReset(edge.id)}
              className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100 col-span-2"
            ><RefreshCw size={11} /> Reset to default</button>
          )}
          {isCustom && (
            <button
              data-testid="edge-delete"
              onClick={() => onDelete(edge.id)}
              className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100 col-span-2"
            ><Trash2 size={11} /> Delete connection</button>
          )}
        </div>
      )}
      {!editMode && (
        <p className="text-[10px] text-slate-400 italic mt-2">Turn on Edit mode to modify direction / propagation / notes.</p>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="rounded-md ring-1 ring-slate-200 bg-white p-3">
      <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Legend</h3>
      <div className="space-y-1.5 text-[11px]">
        {Object.entries(GROUP_STYLES).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded border-2 inline-block" style={{ background: v.bg, borderColor: v.border }} />
            <span className="capitalize text-slate-700">{k}</span>
          </div>
        ))}
        <hr className="my-2" />
        {Object.entries(PROP_STYLES).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-6 h-0.5 inline-block" style={{ background: v.stroke }} />
            <span className="text-slate-700">{k} {k === 'auto' && '(animated)'}</span>
          </div>
        ))}
        <hr className="my-2" />
        <p className="text-slate-500 italic">Click any node or edge to inspect. Enable Edit mode to drag-connect.</p>
      </div>
    </div>
  );
}

function AuditPanel({ audit, compact }) {
  const [expand, setExpand] = useState(!compact);
  // Sync expand state when the parent switches tabs (compact toggles
  // diagram→audit). Without this useEffect the panel keeps whatever
  // state it had on first mount, so jumping to the Audit tab leaves
  // the findings collapsed.
  useEffect(() => { setExpand(!compact); }, [compact]);
  const sevIcon = { high: AlertTriangle, warn: AlertCircle, info: Info };
  const sevTone = { high: 'rose', warn: 'amber', info: 'sky' };
  // Helper: serialise the finding's fix_link payload into a URL the
  // app can router-navigate to. We just hard-navigate via location.href
  // so any page's mount logic re-reads the query params cleanly (this
  // is cheaper than threading useNavigate through every consumer).
  const buildFixHref = (link) => {
    if (!link?.route) return null;
    const qs = new URLSearchParams(link.query || {}).toString();
    return qs ? `${link.route}?${qs}` : link.route;
  };
  return (
    <div className="rounded-md ring-1 ring-slate-200 bg-white" data-testid="linkage-audit-panel">
      <button
        onClick={() => setExpand((v) => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 text-sm font-bold text-[#0F172A] hover:bg-slate-50 rounded-t-md"
      >
        <ChevronDown size={14} className={`transition-transform ${expand ? '' : '-rotate-90'}`} />
        Audit findings
        <span className="ml-2 inline-flex items-center gap-1.5 text-[11px]">
          {audit.summary.high > 0 && <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold">{audit.summary.high} high</span>}
          {audit.summary.warn > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{audit.summary.warn} warn</span>}
          {audit.summary.info > 0 && <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold">{audit.summary.info} info</span>}
          {audit.summary.total === 0 && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">all clean ✓</span>}
        </span>
        <span className="ml-auto text-[10px] text-slate-400 italic">scanned {new Date(audit.scanned_at).toLocaleString()}</span>
      </button>
      {expand && (
        <div className="p-3 pt-1 space-y-2">
          {audit.findings.map((f) => {
            const Icon = sevIcon[f.severity] || Info;
            const tone = sevTone[f.severity] || 'sky';
            return (
              <div key={f.id} data-testid={`audit-finding-${f.id}`} className={`p-3 rounded ring-1 bg-${tone}-50 ring-${tone}-200 text-${tone}-900`} style={{ backgroundColor: tone === 'rose' ? '#FEF2F2' : tone === 'amber' ? '#FFFBEB' : '#F0F9FF', borderColor: tone === 'rose' ? '#FECACA' : tone === 'amber' ? '#FDE68A' : '#BAE6FD' }}>
                <div className="flex items-start gap-2">
                  <Icon size={16} style={{ color: tone === 'rose' ? '#DC2626' : tone === 'amber' ? '#D97706' : '#0284C7' }} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold">{f.title}</div>
                    <p className="text-xs mt-0.5">{f.detail}</p>
                    {f.examples && f.examples.length > 0 && (
                      <div className="text-[11px] mt-1 italic">e.g. {f.examples.join(', ')}</div>
                    )}
                    <div className="text-[11px] mt-1 font-semibold">→ {f.action}</div>
                    {f.fix_link && (
                      <a
                        href={buildFixHref(f.fix_link)}
                        data-testid={`audit-fix-${f.id}`}
                        className="inline-flex items-center gap-1 mt-2 px-2 py-1 text-[11px] font-semibold rounded bg-white ring-1 ring-current hover:bg-current/10 transition-colors"
                        style={{ color: tone === 'rose' ? '#9F1239' : tone === 'amber' ? '#92400E' : '#075985' }}
                      >
                        <ExternalLink size={10} /> {f.fix_link.label || 'Jump to fix'}
                      </a>
                    )}
                  </div>
                  {f.node_id && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/60 text-slate-600">{f.node_id}</span>
                  )}
                </div>
              </div>
            );
          })}
          {audit.findings.length === 0 && (
            <div className="p-4 text-center text-emerald-700 text-sm">No gaps detected — your linkage is clean ✨</div>
          )}
        </div>
      )}
    </div>
  );
}
